/**
 * Общие куски «Голосового стенда»: лента разговора (реплики с паузой робота и вызовы инструментов) и расход —
 * одинаково в живом разговоре и в карточке из журнала. Спека `docs/specs/2026-10-26-voice-lab-design.md`.
 */
import { useState } from "react";

import { SlotTime } from "@/components/zapis/SlotTime";
import type { LabCost, LabEvent, LabKbCard } from "@/lib/voiceLab";

export const ДОЛГО = 1500;

export function usd(v: number): string {
  return `$${v.toFixed(v < 0.1 ? 4 : 2)}`;
}

/** Рубли по курсу: с копейками до 1000 ₽, дальше — до рубля; совсем мелочь — «<0,01 ₽». */
export function rub(v: number, курс: number): string {
  const р = v * курс;
  if (р > 0 && р < 0.01) return "<0,01 ₽";
  const знаков = р < 1000 ? 2 : 0;
  return `${р.toLocaleString("ru-RU", { minimumFractionDigits: знаков, maximumFractionDigits: знаков })} ₽`;
}

type Шаг = Extract<LabEvent, { type: "turn" } | { type: "tool" }>;
type ШагИнструмента = Extract<LabEvent, { type: "tool" }>;

const TOOL: Record<string, string> = { dial: "звонок" };
/** Инструменты поиска по базе знаний — у них в карточке разговора карточки базы со ссылками. */
export const ПОИСК = new Set(["find_answer", "search_knowledge"]);
const НЕТ_ОТВЕТА = ["В базе ответа нет", "В базе ответа не нашлось"];

function KbCardLink({ c }: { c: LabKbCard }) {
  const ниже = c.used === false;
  return (
    <a
      href={`/knowledge/${c.id}`}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex max-w-full items-baseline gap-1.5 rounded-[var(--radius-ui)] border px-2 py-0.5 hover:border-accent ${ниже ? "border-dashed border-border text-fg-muted" : "border-border text-fg"}`}
      title={`${ниже ? "Ниже порога сходства 0,5 — модели не ушла. " : ""}Открыть карточку базы знаний в новой вкладке`}
    >
      <span className="shrink-0 tabular-nums text-fg-muted">#{c.id}</span>
      <span className="min-w-0 truncate">{c.title ?? "карточка"}</span>
      {c.score != null && (
        <span className={`tabular-nums ${ниже ? "" : "text-fg-muted"}`}>
          {c.score.toFixed(2).replace(".", ",")}
        </span>
      )}
      {c.rev != null && <span className="text-fg-muted">r{c.rev}</span>}
    </a>
  );
}

/** Мелкие суммы в деталях — со значащими цифрами: «0,0000064 ₽», а не «< 0,01 ₽». */
function точно(v: number, курс?: number): string {
  const x = курс ? v * курс : v;
  const т = x.toLocaleString("ru-RU", { maximumSignificantDigits: 2 });
  return курс ? `${т} ₽` : `$${т}`;
}

/** Шаг поиска по базе знаний: запрос, мс и расход, карточки за ответом ссылками в редактор, текст модели. */
function KnowledgeStep({
  e,
  seek,
  курс,
}: {
  e: ШагИнструмента;
  seek?: ((t: number) => void) | undefined;
  курс?: number | undefined;
}) {
  const вопрос = String(e.args.question ?? "");
  const lightrag = e.name === "search_knowledge";
  const нет = НЕТ_ОТВЕТА.some((n) => e.result.startsWith(n));
  const карточки = e.cards ?? [];
  const ушли = карточки.filter((c) => c.used !== false);
  const ниже = карточки.filter((c) => c.used === false);
  const деньги = e.embed?.usd;
  return (
    <div
      className="flex flex-col gap-1.5 rounded-[var(--radius-ui)] border border-border px-3 py-2 text-[12px] [overflow-wrap:anywhere] sm:ml-6"
      data-annot="поиск по базе знаний"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 text-fg-muted">
        <Метка t={e.t} seek={seek} />
        <span className="font-medium text-fg">{e.name}</span>
        <span>{lightrag ? "LightRAG" : "база знаний"}</span>
        <span className="tabular-nums">· {e.ms.toLocaleString("ru-RU")} мс</span>
        <span className="tabular-nums">
          ·{" "}
          {lightrag || e.restored || !e.embed
            ? "сумма поиска неизвестна"
            : e.embed.error
              ? "эмбеддинг не ответил"
              : деньги != null
                ? `эмбеддинг ${курс ? rub(деньги, курс) : usd(деньги)}`
                : "сумма эмбеддинга не пришла"}
        </span>
        {e.cached && <span>· тот же вопрос — из кэша разговора</span>}
      </div>
      <div>
        <span className="text-fg-muted">Запрос: </span>«{вопрос}»
      </div>
      {нет ? (
        <div>
          <b>Ответ не найден</b>
          <span className="text-fg-muted">
            {e.embed?.error
              ? " — эмбеддинг вопроса не успел (сбой, а не отсутствие знания)"
              : ниже.length
                ? " — ближайшие карточки ниже порога сходства 0,5:"
                : " — причина в журнале не указана"}
          </span>{" "}
          {!ниже.length && (
            <a href="/knowledge" target="_blank" rel="noreferrer" className="text-accent hover:underline">
              Открыть базу знаний ↗
            </a>
          )}
        </div>
      ) : (
        карточки.length > 0 && (
          <div className="text-fg-muted">
            {e.restored
              ? `Восстановлено ${ушли.length} — по заголовкам в ответе; полнота неизвестна`
              : `${ушли.length} ${ушли.length === 1 ? "карточка ушла" : "карточки ушли"} модели${ниже.length ? ` · ${ниже.length} ниже порога` : ""}`}
          </div>
        )
      )}
      {карточки.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {[...ушли, ...ниже].map((c) => (
            <KbCardLink key={c.id} c={c} />
          ))}
        </div>
      )}
      <details>
        <summary className="cursor-pointer text-fg-muted hover:text-fg">Детали поиска и ответ модели</summary>
        <div className="mt-1 flex flex-col gap-1 text-fg-muted">
          <div>
            {lightrag
              ? "LightRAG /query/data, режим naive, без генерации; карточки — по документам prokat-kb-<id>-r<ревизия>; сходство и стоимость сервис не отдаёт."
              : e.restored
                ? "Звонок до 27.10.26: карточки узнаны по заголовкам в ответе, сходство и расход эмбеддинга не записывались."
                : `Эмбеддинг вопроса · OpenRouter${e.embed ? ` · ${e.embed.tokens ?? "?"} ток. · ${e.embed.ms} мс${деньги != null ? ` · ${точно(деньги, курс)}` : ""}` : ""} · весь шаг ${e.ms} мс. До 3 карточек города и общих, порог сходства 0,5. Сходство — не вероятность правильного ответа.`}
          </div>
          <div className="whitespace-pre-wrap">{e.result}</div>
        </div>
      </details>
    </div>
  );
}

/** Строка инструмента в ленте: поиск по базе — подробно, остальные — одной строкой. */
/** «26.10» из `ГГГГ-ММ-ДД`. */
const дм = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;

/** Ссылка шага free_slots: «Время записи» салона на день, который смотрел робот, — сейчас, а не на момент
 *  звонка. Окно открывается под шагом (только посмотреть, без текста клиенту). */
function ПроверитьСлоты({ день, салон }: { день: string | null; салон: number | null }) {
  const [открыто, setОткрыто] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setОткрыто((v) => !v)}
        aria-expanded={открыто}
        title="Загрузка салона по часам сейчас — те же слоты CRM, что читает робот"
        className="ml-1 text-accent-text hover:underline"
      >
        {открыто ? "скрыть слоты" : `перепроверить слоты${день ? ` на ${дм(день)}` : ""} ↗`}
      </button>
      {открыто && (
        <div className="mt-1.5 rounded-[var(--radius-ui)] border border-border bg-bg p-2 text-fg" data-annot="слоты free_slots">
          <SlotTime locationId={салон} day={день} wide />
        </div>
      )}
    </>
  );
}

export function ToolStep({
  e,
  seek,
  курс,
  салон,
}: {
  e: ШагИнструмента;
  seek?: ((t: number) => void) | undefined;
  курс?: number | undefined;
  /** салон звонка — для free_slots, если шаг его не записал (звонки до 03.11.26) */
  салон?: number | null | undefined;
}) {
  const сбой = e.error ? (
    <div className="text-err" data-annot="ошибка инструмента">
      ⚠ ошибка: {e.error} — модель получила заглушку
    </div>
  ) : null;
  if (ПОИСК.has(e.name))
    return (
      <>
        <KnowledgeStep e={e} seek={seek} курс={курс} />
        {сбой && <div className="px-3 text-[12px] sm:ml-6">{сбой}</div>}
      </>
    );
  return (
    <div
      className={`rounded-[var(--radius-ui)] border px-3 py-1.5 text-[12px] text-fg-muted [overflow-wrap:anywhere] sm:ml-6 ${e.error ? "border-err/70" : "border-border"}`}
    >
      <Метка t={e.t} seek={seek} />⚙ {TOOL[e.name] ?? e.name}(
      {Object.values(e.args)
        .filter((v) => v != null && v !== "")
        .join(", ")}
      ) · {e.ms} мс → {e.result}
      {e.name === "free_slots" && (
        <ПроверитьСлоты день={typeof e.args.day === "string" ? e.args.day : null} салон={e.location_id ?? салон ?? null} />
      )}
      {сбой}
    </div>
  );
}

/** 75 → «1:15» — секунда записи. */
export function мс(t: number): string {
  const s = Math.max(0, Math.floor(t));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Секунда записи у реплики: клик — перемотать плеер карточки (`seek`), без плеера — просто метка. */
export function Метка({
  t,
  seek,
}: {
  t?: number | null | undefined;
  seek?: ((t: number) => void) | undefined;
}) {
  if (t == null) return null;
  if (!seek)
    return (
      <span className="mr-2 text-[11px] tabular-nums text-fg-muted">
        {мс(t)}
      </span>
    );
  return (
    <button
      type="button"
      onClick={() => seek(t)}
      className="mr-2 text-[11px] tabular-nums text-accent hover:underline"
      title="Перемотать запись сюда"
    >
      {мс(t)}
    </button>
  );
}

export function Transcript({
  items,
  seek,
}: {
  items: Шаг[];
  seek?: ((t: number) => void) | undefined;
}) {
  if (!items.length) return null;
  return (
    <section
      className="flex max-w-3xl flex-col gap-2"
      data-annot="лента разговора"
    >
      {items.map((e, i) =>
        e.type === "tool" ? (
          <ToolStep key={i} e={e} seek={seek} />
        ) : (
          <div
            key={i}
            className={`flex gap-2 text-[13px] ${e.who === "agent" ? "" : "justify-end"}`}
          >
            <div
              className={`max-w-[80%] rounded-[var(--radius-ui)] px-3 py-1.5 ${e.who === "agent" ? "bg-bg-subtle" : "bg-accent/10"}`}
            >
              <Метка t={e.t} seek={seek} />
              {e.text || "…"}
              {e.interrupted && (
                <span
                  className="ml-2 text-[11px] text-err"
                  title="Звук оборвался раньше текста — что прозвучало, смотрите в расшифровке записи"
                >
                  перебили
                </span>
              )}
              {e.echo && (
                <span
                  className="ml-2 text-[11px] text-err"
                  title="Собеседник «сказал» слова робота сразу после него — эхо в трубке, ложное перебивание"
                >
                  эхо
                </span>
              )}
              {e.phrase && (
                <span className="ml-2 text-[11px] text-accent" title="Прозвучала готовой записью из библиотеки фраз">
                  запись
                </span>
              )}
              {e.who === "agent" && e.latency_ms != null && (
                <span
                  className={`ml-2 text-[11px] ${e.latency_ms > ДОЛГО ? "text-err" : "text-fg-muted"}`}
                >
                  пауза {e.latency_ms} мс
                </span>
              )}
            </div>
          </div>
        ),
      )}
    </section>
  );
}

export function CostBlock({ cost, live }: { cost: LabCost; live: boolean }) {
  return (
    <section
      className="flex max-w-3xl flex-col gap-1 text-[12.5px]"
      data-annot="расход разговора"
    >
      <div className="font-medium">
        {live ? "Расход с начала разговора" : "Расход разговора"}:{" "}
        {cost.usd != null ? usd(cost.usd) : "цена модели не задана"}
        <span className="ml-2 font-normal text-fg-muted">
          {cost.seconds} с
          {cost.usd != null && cost.seconds > 0
            ? ` · ${usd((cost.usd * 60) / cost.seconds)} за минуту`
            : ""}
        </span>
      </div>
      <table className="w-fit">
        <tbody>
          {cost.rows.map((r) => (
            <tr key={r.what} className="text-fg-muted">
              <td className="pr-4">{r.what}</td>
              <td className="pr-4 text-right tabular-nums">
                {r.tokens != null ? r.tokens.toLocaleString("ru-RU") : ""}
              </td>
              <td className="text-right tabular-nums">
                {r.usd != null ? usd(r.usd) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {cost.source && (
        <div className="text-[11.5px] text-fg-muted">цены: {cost.source}</div>
      )}
    </section>
  );
}
